// ダッシュボード（ログイン後の着地画面, 設計 §3）。
// 実装済み API のみを根拠に、誇張・モックなしで現状を表示する（コスト/セキュリティ可視化は後続フェーズ）。
// 認可の正本はサーバ側（R1/R5）。SPA は表示と遷移のみ。
import type { ApiClient } from "../api/client";
import { ApiError } from "../api/client";
import type { ShellRoute } from "../layout/app-shell";

export interface DashboardDeps {
  readonly apiClient: ApiClient;
  readonly onNavigate: (route: ShellRoute) => void;
}

interface IpAllowlistResponse {
  readonly items: ReadonlyArray<{ readonly cidr: string }>;
}

export function renderDashboard(root: HTMLElement, deps: DashboardDeps): void {
  root.replaceChildren();

  const lead = document.createElement("p");
  lead.className = "text-sm text-slate-600";
  lead.textContent = "クラウドのコスト可視化とセキュリティ監査の管理コンソールです。";

  const grid = document.createElement("div");
  grid.className = "mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3";

  // 許可IP: 実 API（/tenant/ip-allowlist）から件数を取得して表示する。
  const ipCard = buildCard({
    title: "許可IP",
    value: "—",
    actionLabel: "テナント管理で管理",
    onAction: () => deps.onNavigate("tenant-admin"),
  });
  // メンバー招待: 任意（必須のオンボード手順ではない）。
  const inviteCard = buildCard({
    title: "メンバー招待（任意）",
    value: "必要に応じて",
    actionLabel: "招待を管理",
    onAction: () => deps.onNavigate("tenant-admin"),
  });
  grid.append(ipCard.card, inviteCard.card);

  // 今後追加予定: まだ実装されていない機能は情報表示のみ（動かないボタンは置かない）。
  const upcoming = document.createElement("section");
  upcoming.className = "mt-8";
  upcoming.setAttribute("aria-labelledby", "upcoming-heading");
  const uh = document.createElement("h2");
  uh.id = "upcoming-heading";
  uh.className = "text-lg font-medium text-slate-900";
  uh.textContent = "今後追加予定";
  const ul = document.createElement("ul");
  ul.className = "mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600";
  for (const text of ["クラウド連携（準備中）", "コスト可視化（準備中）", "セキュリティ監査（準備中）"]) {
    const li = document.createElement("li");
    li.textContent = text;
    ul.append(li);
  }
  upcoming.append(uh, ul);

  const alert = document.createElement("div");
  alert.setAttribute("role", "alert");
  alert.setAttribute("aria-live", "polite");
  alert.className = "mt-4 min-h-[1.25rem] text-sm text-red-700";

  root.append(lead, grid, upcoming, alert);

  // 許可IP 件数を取得（失敗してもダッシュボード全体は壊さない。401 はクライアントが再ログイン誘導）。
  deps.apiClient
    .get<IpAllowlistResponse>("/tenant/ip-allowlist")
    .then((res) => {
      ipCard.value.textContent = String(res.items.length);
    })
    .catch((err: unknown) => {
      ipCard.value.textContent = "—";
      if (err instanceof ApiError && err.status === 403) {
        // 権限が無い場合はカードを控えめに（エラーバナーは出さない）。
        ipCard.value.textContent = "—";
      } else {
        alert.textContent = "一部の情報を取得できませんでした。";
      }
    });
}

interface CardProps {
  readonly title: string;
  readonly value: string;
  readonly actionLabel: string;
  readonly onAction: () => void;
}

function buildCard(props: CardProps): { card: HTMLElement; value: HTMLElement } {
  const card = document.createElement("article");
  card.className = "rounded-lg border border-slate-200 p-4";

  const title = document.createElement("h2");
  title.className = "text-sm font-medium text-slate-600";
  title.textContent = props.title;

  const value = document.createElement("p");
  value.className = "mt-1 text-2xl font-semibold text-slate-900";
  value.textContent = props.value;

  const action = document.createElement("button");
  action.type = "button";
  action.className =
    "mt-3 text-sm font-medium text-shinonome-700 hover:text-shinonome-800 hover:underline";
  action.textContent = props.actionLabel;
  action.addEventListener("click", () => props.onAction());

  card.append(title, value, action);
  return { card, value };
}
